import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@trillion/ui/components/ui/sheet";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { jobsApi } from "@/lib/admin-api";
import type { FailedJob, JobStateHistory } from "@/lib/jobs-types";
import { fmt } from "./run-columns";

interface ErrorInspectorProps {
  /** The failed job to inspect; the sheet is open while non-null. */
  job: FailedJob | null;
  onOpenChange: (open: boolean) => void;
}

// Renders a failed run's exception detail (from the list row) plus its state
// history fetched on demand from `GET /admin/jobs/{id}`.
export function ErrorInspector({ job, onOpenChange }: ErrorInspectorProps) {
  const [history, setHistory] = useState<JobStateHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!job) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    jobsApi
      .details(job.job_id)
      .then((d) => {
        if (!cancelled) setHistory(d.history ?? []);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(err instanceof Error ? err.message : "Failed to load job history");
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [job]);

  return (
    <Sheet open={job !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Failed run</SheetTitle>
          <SheetDescription className="font-mono text-xs">{job?.job_id}</SheetDescription>
        </SheetHeader>

        {job && (
          <div className="flex flex-col gap-6 px-4 pb-6">
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">Exception</h3>
              {job.exception_type ? (
                <Badge variant="outline" className="w-fit text-destructive">
                  {job.exception_type}
                </Badge>
              ) : null}
              {job.exception_message && (
                <p className="text-sm text-muted-foreground">{job.exception_message}</p>
              )}
              {job.reason && job.reason !== job.exception_message && (
                <p className="text-sm text-muted-foreground">{job.reason}</p>
              )}
              <p className="text-xs text-muted-foreground">Failed at {fmt(job.failed_at)}</p>
            </section>

            {job.exception_details && (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Details</h3>
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-3 font-mono text-xs">
                  {job.exception_details}
                </pre>
              </section>
            )}

            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">State history</h3>
              {loadingHistory ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Loading…
                </div>
              ) : history.length ? (
                <ol className="flex flex-col gap-2">
                  {history.map((h, i) => (
                    <li key={`${h.state_name}-${i}`} className="rounded border p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{h.state_name}</Badge>
                        <span className="text-xs text-muted-foreground">{fmt(h.created_at)}</span>
                      </div>
                      {h.reason && (
                        <p className="mt-1 text-xs text-muted-foreground">{h.reason}</p>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-muted-foreground">No state history.</p>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
